import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Check, XCircle, Download } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

const RAG = { green: "bg-green-100 text-green-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700", grey: "bg-zinc-100 text-zinc-500" };
const RAG_ICON = { green: "✅", amber: "⚠️", red: "🔴", grey: "⬜" };

export default function QcomHealthPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: d } = await api.get("/sla/health", { params });
      setData(d);
    } catch (err) { console.error("API get /sla/health error:", err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const s = data?.summary || {};
  const orders = data?.orders || [];

  return (
    <div data-testid="qcom-health-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Qcom Order Health</h1>
          <p className="text-sm text-zinc-500 mt-1">SLA monitoring for Quick Commerce orders</p>
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex gap-3 mb-4">
        <span className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-sm text-green-700 text-xs font-semibold">✅ {s.on_track || 0} on track</span>
        <span className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-700 text-xs font-semibold">⚠️ {s.at_risk || 0} at risk</span>
        <span className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-sm text-red-700 text-xs font-semibold">🔴 {s.breached || 0} breached</span>
      </div>

      <div className="flex gap-3 mb-4">
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs rounded-sm w-36" placeholder="From" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs rounded-sm w-36" placeholder="To" />
      </div>

      <div className="border border-zinc-200 overflow-x-auto" data-testid="qcom-health-table">
        <table className="data-table w-full">
          <thead><tr><th>Order #</th><th>Customer</th><th>Sales Channel</th><th>Current Stage</th><th className="num">Working Mins</th><th className="num">SLA Limit</th><th>SLA Status</th><th>Deadline</th><th>Late Punch</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            orders.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">No active Qcom orders</td></tr> :
            orders.map(o => (
              <tr key={o._id} className="cursor-pointer hover:bg-zinc-50" onClick={() => navigate(`/orders/${o._id}`)} data-testid={`health-row-${o._id}`}>
                <td className="font-mono text-xs font-semibold">{o.order_number}</td>
                <td className="text-xs">{o.customer_name}</td>
                <td className="text-xs">{o.channel_name}</td>
                <td className="text-xs">{o.status}</td>
                <td className="num font-mono text-xs">{Math.floor(o.current_stage_mins / 60)}h {o.current_stage_mins % 60}m</td>
                <td className="num font-mono text-xs text-zinc-500">{Math.floor(o.current_stage_threshold / 60)}h {o.current_stage_threshold % 60}m</td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${RAG[o.sla_rag]}`}>{RAG_ICON[o.sla_rag]} {o.sla_rag.toUpperCase()}</span></td>
                <td className="text-[10px] text-zinc-500">{o.sla_deadline ? fmtDateTime(o.sla_deadline) : "—"}</td>
                <td>{o.late_punch && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded-sm">LATE</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
